import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useResolve } from '@/infrastructure/useResolve';
import { type ILogger, TOKEN_ILogger } from '@/services/Logger';


// Error boundaries can only be class components. Function wrapper exists to keep DI usage
// (useResolve) outside the class.

interface BoundaryProps {
  logger: ILogger;
  children: ReactNode;
}

class RawErrorBoundary extends Component<BoundaryProps, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.logger.error('Render error in', info.componentStack, error);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-3 p-6 text-center">
          <h1 className="text-lg font-semibold">Something went wrong.</h1>
          <pre className="max-w-2xl overflow-auto rounded bg-slate-100 p-3 text-left text-xs text-slate-800 dark:bg-slate-800 dark:text-slate-200">
            {this.state.error.message}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded bg-slate-900 px-3 py-1.5 text-sm text-white"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function ErrorBoundaryView({ children }: { children: ReactNode }) {
  const logger = useResolve(TOKEN_ILogger);
  return <RawErrorBoundary logger={logger}>{children}</RawErrorBoundary>;
}
